import {
  CrunchAccountServiceWithContext,
  formatMicroUsdcToUsdc,
  getPayoutClaimRecordAddress,
} from "@crunchdao/sdk";
import { BaseCommand } from "@crunchdao/shared-cli";
import { formatAddressWithLink } from "@crunchdao/shared-cli";
import { createTable } from "@crunchdao/shared-cli";
import { isAccountDoesNotExistError } from "@crunchdao/sdk";
import chalk from "chalk";
import { PublicKey } from "@solana/web3.js";

/**
 * Command to get detailed information about a cruncher registered in a crunch competition
 *
 * This command displays:
 * - Cruncher basic information (address, name, index)
 * - Payout claim record details
 * - All models registered by the cruncher
 * - Active state of each model in the crunch
 */
export class GetCruncherCommand extends BaseCommand {
  async execute(crunchName: string, cruncherWallet: string): Promise<void> {
    if (!crunchName) {
      console.error("Error: Crunch name parameter is required");
      process.exit(400);
    }

    if (!cruncherWallet) {
      console.error("Error: Cruncher wallet address parameter is required");
      process.exit(400);
    }

    // Validate cruncher wallet address
    let cruncherWalletAddress: PublicKey;
    try {
      cruncherWalletAddress = new PublicKey(cruncherWallet);
    } catch {
      console.error(`Error: Invalid cruncher wallet address: ${cruncherWallet}`);
      process.exit(400);
    }

    const crunchAccountService = CrunchAccountServiceWithContext({
      program: this.program,
    });

    try {
      // Get crunch address
      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);

      // Derive the Cruncher PDA from the wallet address
      const cruncherAddress = crunchAccountService.getCruncherAddress(cruncherWalletAddress);

      // Fetch cruncher account to get the name
      let cruncherName = "Unknown";
      try {
        const cruncherAccount = await this.program.account.cruncher.fetch(cruncherAddress);
        cruncherName = cruncherAccount.name;
      } catch {
        // Cruncher account might not exist, that's okay
      }

      // Fetch the payout claim record
      let claimRecord = null;
      let cruncherIndex = -1;
      try {
        const claimRecordData = await crunchAccountService.getPayoutClaimRecordForCruncher(
          crunchAddress,
          cruncherAddress
        );
        claimRecord = claimRecordData.account;
        cruncherIndex = claimRecord.cruncherIndex;
      } catch (error) {
        if (isAccountDoesNotExistError(error)) {
          console.error(
            chalk.red(
              `\n❌ Cruncher wallet ${cruncherWalletAddress.toBase58()} (PDA: ${cruncherAddress.toBase58()}) is not registered in crunch "${crunchName}"`
            )
          );
          process.exit(404);
        }
        throw error;
      }

      // Fetch crunch models to find models for this cruncher
      let cruncherModels: any[] = [];
      let crunchModelsAccount = null;
      try {
        crunchModelsAccount = await crunchAccountService.getCrunchModels(crunchAddress);
        const allModels = crunchModelsAccount.account.models;

        // Filter models by cruncher index
        cruncherModels = allModels.filter((model: any) => model.cruncherIndex === cruncherIndex);
      } catch (error) {
        if (!isAccountDoesNotExistError(error)) {
          console.warn("⚠️  Could not fetch crunch models");
        }
      }

      // Fetch model details for each model
      const modelDetails = [];
      for (const activeModel of cruncherModels) {
        try {
          const modelAccount = await crunchAccountService.getModelByAddress(activeModel.modelKey);
          if (modelAccount) {
            modelDetails.push({
              id: modelAccount.id,
              address: activeModel.modelKey.toString(),
              desiredState: Object.keys(activeModel.desiredState)[0] || "unknown",
              signature: modelAccount.signature,
              submissionId: modelAccount.submissionId,
              resourceId: modelAccount.resourceId,
            });
          }
        } catch {
          // If we can't fetch the model account, use minimal info
          modelDetails.push({
            id: "Unknown",
            address: activeModel.modelKey.toString(),
            desiredState: Object.keys(activeModel.desiredState)[0] || "unknown",
            signature: "",
            submissionId: "",
            resourceId: "",
          });
        }
      }

      // Fetch all checkpoints and their prizes for this cruncher
      const checkpointPrizes = [];
      let totalPrizes = 0;
      try {
        const checkpoints = await crunchAccountService.getCheckpointsForCrunch(crunchAddress);

        for (const checkpoint of checkpoints) {
          // Find prize for this cruncher in this checkpoint
          const prize = checkpoint.account.prizes.find(
            (p: any) => p.cruncherIndex === cruncherIndex
          );

          if (prize) {
            const prizeAmount = Number(prize.prize);
            totalPrizes += prizeAmount;

            checkpointPrizes.push({
              checkpointIndex: checkpoint.account.index,
              checkpointAddress: checkpoint.address.toBase58(),
              prizeAmount: prizeAmount,
              prizeAmountFormatted: formatMicroUsdcToUsdc(prizeAmount),
              status: Object.keys(checkpoint.account.status)[0],
              timestamp: new Date(Number(checkpoint.account.timestamp) * 1000).toISOString(),
            });
          }
        }
      } catch (error) {
        if (!isAccountDoesNotExistError(error)) {
          console.warn("⚠️  Could not fetch checkpoints");
        }
      }

      const output = {
        crunch: crunchName,
        crunchAddress: crunchAddress.toBase58(),
        cruncher: {
          name: cruncherName,
          walletAddress: cruncherWalletAddress.toBase58(),
          pdaAddress: cruncherAddress.toBase58(),
          index: cruncherIndex,
        },
        claimRecord: {
          address: getPayoutClaimRecordAddress(crunchAddress, cruncherAddress).toBase58(),
          cruncherIndex: claimRecord.cruncherIndex,
          bump: claimRecord.bump,
        },
        models: {
          count: modelDetails.length,
          details: modelDetails,
        },
        prizes: {
          count: checkpointPrizes.length,
          totalAmount: totalPrizes,
          totalAmountFormatted: formatMicroUsdcToUsdc(totalPrizes),
          details: checkpointPrizes,
        },
      };

      // Output based on format preference
      if (this.resolvedOptions.output === "json") {
        this.output(output);
      } else {
        this.displayCruncherInfo(output);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("Account does not exist")) {
        console.error(chalk.red(`\n❌ Crunch "${crunchName}" not found`));
        process.exit(404);
      }
      throw error;
    }
  }

  private displayCruncherInfo(data: any): void {
    // Header
    console.log(chalk.cyan.bold(`\nCruncher Information`));
    console.log(chalk.gray("═".repeat(80)));

    // Crunch info
    console.log(chalk.yellow("\nCrunch:"));
    console.log(`  Name: ${chalk.white(data.crunch)}`);
    console.log(`  Address: ${formatAddressWithLink(data.crunchAddress, this.network)}`);

    // Cruncher info
    console.log(chalk.yellow("\nCruncher:"));
    const cruncherTable = createTable({ colWidths: [20, 60] });
    cruncherTable.push(
      [chalk.yellow("Name"), chalk.white(data.cruncher.name)],
      [
        chalk.yellow("Wallet Address"),
        formatAddressWithLink(data.cruncher.walletAddress, this.network),
      ],
      [chalk.yellow("PDA Address"), formatAddressWithLink(data.cruncher.pdaAddress, this.network)],
      [chalk.yellow("Index"), chalk.cyan(data.cruncher.index.toString())]
    );
    console.log(cruncherTable.toString());

    // Claim record info
    console.log(chalk.yellow("\nPayout Claim Record:"));
    const claimRecordTable = createTable({ colWidths: [20, 60] });
    claimRecordTable.push(
      [chalk.yellow("Address"), formatAddressWithLink(data.claimRecord.address, this.network)],
      [chalk.yellow("Cruncher Index"), chalk.cyan(data.claimRecord.cruncherIndex.toString())],
      [chalk.yellow("Bump"), chalk.white(data.claimRecord.bump.toString())]
    );
    console.log(claimRecordTable.toString());

    // Models info
    console.log(chalk.yellow("\nModels:"));
    if (data.models.count === 0) {
      console.log(chalk.gray("  No models registered"));
    } else {
      console.log(chalk.white(`  Total models: ${chalk.cyan(data.models.count.toString())}`));
      console.log();

      const modelsTable = createTable({ colWidths: [20, 30, 15, 15] });
      modelsTable.push([
        chalk.yellow("Model ID"),
        chalk.yellow("Address"),
        chalk.yellow("State"),
        chalk.yellow("Submission ID"),
      ]);

      for (const model of data.models.details) {
        const stateColor = this.getStateColor(model.desiredState);
        modelsTable.push([
          chalk.white(model.id || "Unknown"),
          formatAddressWithLink(model.address, this.network),
          stateColor(model.desiredState),
          chalk.gray(model.submissionId || "-"),
        ]);
      }

      console.log(modelsTable.toString());
    }

    // Prizes info
    console.log(chalk.yellow("\nPrizes from Checkpoints:"));
    if (data.prizes.count === 0) {
      console.log(chalk.gray("  No prizes assigned yet"));
    } else {
      console.log(
        chalk.white(
          `  Total prizes: ${chalk.green(data.prizes.totalAmountFormatted)} (${
            data.prizes.count
          } checkpoint${data.prizes.count > 1 ? "s" : ""})`
        )
      );
      console.log();

      const prizesTable = createTable({ colWidths: [10, 30, 15, 20] });
      prizesTable.push([
        chalk.yellow("CP #"),
        chalk.yellow("Checkpoint Address"),
        chalk.yellow("Prize"),
        chalk.yellow("Date"),
      ]);

      for (const prize of data.prizes.details) {
        prizesTable.push([
          chalk.cyan(prize.checkpointIndex.toString()),
          formatAddressWithLink(prize.checkpointAddress, this.network),
          chalk.green(prize.prizeAmountFormatted),
          chalk.gray(new Date(prize.timestamp).toLocaleDateString()),
        ]);
      }

      console.log(prizesTable.toString());
    }

    console.log(chalk.gray("\n" + "═".repeat(80)));
  }

  private getStateColor(state: string): (text: string) => string {
    switch (state.toLowerCase()) {
      case "active":
        return chalk.green;
      case "inactive":
        return chalk.red;
      case "paused":
        return chalk.yellow;
      default:
        return chalk.white;
    }
  }
}
